/**
 * importSelection.ts
 *
 * Turns the output of buildImportPayload (a full, unfiltered diff) into a
 * reviewable, user-selectable change plan and then filters the payload down to
 * only the selected changes — without disturbing the diff engine itself.
 *
 *   planImport(result, snapshot, target)      → ImportPlan (for the review UI)
 *   defaultSelectedIds(plan)                  → Set<string> (creates + updates on, deletes off)
 *   filterImportResult(result, target, ids)   → ImportPayloadResult (only selected rows)
 *
 * Every insert / update / delete row produced by the diff engine is attributed
 * to exactly one ChangeItem via a deterministic key so that filtering can never
 * produce an FK-inconsistent payload.
 */
import { ChangeItem, ImportPlan, DeletePayload } from '../types/snapshot';
import { TargetEnvData, ImportPayloadResult } from './snapshotService';

// ─────────────────────────────────────────────────────────────────────────────
// Stable ChangeItem id builders
// ─────────────────────────────────────────────────────────────────────────────

const entKey = (name: unknown, value: unknown) => `${name ?? ''}||${value ?? ''}`;
// Entitlement key includes needs_quantity to disambiguate same name+value entitlements.
const entKeyQ = (name: unknown, value: unknown, needsQuantity: unknown) =>
  `${name ?? ''}||${value ?? ''}||${needsQuantity ?? ''}`;
const orgEntKey = (name: unknown, value: unknown, accessType: unknown) =>
  `${name ?? ''}||${value ?? ''}||${accessType ?? ''}`;

const ids = {
  productCreate: 'product:create',
  productUpdate: 'product:update',
  entCreate: (k: string) => `ent:create:${k}`,
  entUpdate: (k: string) => `ent:update:${k}`,
  entDelete: (k: string) => `ent:delete:${k}`,
  orgEntCreate: (k: string) => `orgent:create:${k}`,
  orgEntUpdate: (k: string) => `orgent:update:${k}`,
  orgEntDelete: (k: string) => `orgent:delete:${k}`,
  regionCreate: (n: string) => `region:create:${n}`,
  regionDelete: (n: string) => `region:delete:${n}`,
  questionCreate: (n: string) => `question:create:${n}`,
  questionUpdate: (n: string) => `question:update:${n}`,
  questionDelete: (n: string) => `question:delete:${n}`,
  skuCreate: (n: string) => `sku:create:${n}`,
  skuUnlink: (n: string) => `sku:unlink:${n}`,
  skuLinkChange: (n: string) => `sku:linkchange:${n}`,
};

// ─────────────────────────────────────────────────────────────────────────────
// Internal lookup maps derived from the payload + target env
// ─────────────────────────────────────────────────────────────────────────────

interface LookupMaps {
  /** SKU id → name (from new sku inserts + existing target skus) */
  skuIdToName: Map<number, string>;
  /** Newly-created entitlement id → its ChangeItem key (name||value) */
  newEntIdToKey: Map<number, string>;
  /** Newly-created org entitlement id → its ChangeItem key (name||value||access_type) */
  newOrgEntIdToKey: Map<number, string>;
  /** Entitlement id queued for deletion → its ChangeItem key (name||value) */
  entDeleteIdToKey: Map<number, string>;
  /** Org entitlement id queued for deletion → its ChangeItem key (name||value) */
  orgEntDeleteIdToKey: Map<number, string>;
  /** SKU names being fully unlinked from the main product */
  unlinkSkuNames: Set<string>;
}

const num = (v: unknown): number | undefined =>
  typeof v === 'number' ? v : undefined;
const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

function buildLookups(
  result: ImportPayloadResult,
  target: TargetEnvData,
): LookupMaps {
  const skuIdToName = new Map<number, string>();
  target.skus.forEach((s) => {
    if (s.id !== undefined) { skuIdToName.set(s.id, s.name); }
  });

  const newEntIdToKey = new Map<number, string>();
  const newOrgEntIdToKey = new Map<number, string>();

  result.insertPayload.bulk_entry.forEach((group) => {
    if (group.table_key === 'skus') {
      group.entries.forEach((e) => {
        const id = num(e.data.ID);
        if (id !== undefined) { skuIdToName.set(id, str(e.data.Name)); }
      });
    } else if (group.table_key === 'entitlements') {
      group.entries.forEach((e) => {
        const id = num(e.data.ID);
        if (id !== undefined) {
          newEntIdToKey.set(id, entKeyQ(e.data.Name, e.data.Value, e.data.NeedsQuantity));
        }
      });
    } else if (group.table_key === 'org_entitlements') {
      group.entries.forEach((e) => {
        const id = num(e.data.ID);
        if (id !== undefined) {
          newOrgEntIdToKey.set(
            id,
            orgEntKey(e.data.Name, e.data.Value, e.data.AccessType),
          );
        }
      });
    }
  });

  const entDeleteIdToKey = new Map<number, string>();
  result.deletePayload.entitlementsToDelete.forEach((d) => {
    entDeleteIdToKey.set(d.id, entKeyQ(d.name, d.value, d.needs_quantity));
  });
  const orgEntDeleteIdToKey = new Map<number, string>();
  result.deletePayload.orgEntitlementsToDelete.forEach((d) => {
    orgEntDeleteIdToKey.set(d.id, entKey(d.name, d.value));
  });

  const unlinkSkuNames = new Set(
    result.deletePayload.skuLinksToRemove.map((d) => d.skuName),
  );

  return {
    skuIdToName,
    newEntIdToKey,
    newOrgEntIdToKey,
    entDeleteIdToKey,
    orgEntDeleteIdToKey,
    unlinkSkuNames,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// planImport — derive the reviewable ChangeItem plan
// ─────────────────────────────────────────────────────────────────────────────

export function planImport(
  result: ImportPayloadResult,
  target: TargetEnvData,
): ImportPlan {
  const maps = buildLookups(result, target);
  const creates: ChangeItem[] = [];
  const updates: ChangeItem[] = [];
  const deletes: ChangeItem[] = [];

  // ── Creates from the insert payload ──────────────────────────────────────
  const skuCreateNames = new Set<string>();
  // sku name → set of dependency ChangeItem ids (new entitlements/org ents it links)
  const skuDeps = new Map<string, Set<string>>();

  // An entitlement and org entitlement represent the same product entitlement
  // only when all of their identifying fields match within that product.
  // Keep the org-entitlement change ids by inserted entitlement id so selecting
  // a SKU also selects the corresponding org-entitlement change.
  const orgEntChanges = [
    ...result.insertPayload.bulk_entry
      .filter((group) => group.table_key === 'org_entitlements')
      .flatMap((group) => group.entries.map((entry) => ({
        changeId: ids.orgEntCreate(orgEntKey(
          entry.data.Name,
          entry.data.Value,
          entry.data.AccessType,
        )),
        data: entry.data,
      }))),
    ...(result.updatePayload?.bulk_update ?? [])
      .filter((group) => group.table_key === 'org_entitlements')
      .flatMap((group) => group.updates.map((update) => ({
        changeId: ids.orgEntUpdate(orgEntKey(
          update.data.Name,
          update.data.Value,
          update.data.AccessType,
        )),
        data: update.data,
      }))),
  ];
  const matchingOrgEntChangeIdsByEntId = new Map<number, string[]>();
  result.insertPayload.bulk_entry
    .filter((group) => group.table_key === 'entitlements')
    .flatMap((group) => group.entries)
    .forEach((entitlement) => {
      const entitlementId = num(entitlement.data.ID);
      if (entitlementId === undefined) { return; }

      const matchingChanges = orgEntChanges
        .filter(({ data }) =>
          num(data.ProductID) !== undefined &&
          num(data.ProductID) === num(entitlement.data.ProductID) &&
          str(data.Name) === str(entitlement.data.Name) &&
          str(data.Value) === str(entitlement.data.Value) &&
          Boolean(data.NeedsQuantity) === Boolean(entitlement.data.NeedsQuantity) &&
          str(data.Unit) === str(entitlement.data.Unit),
        )
        .map(({ changeId }) => changeId);

      if (matchingChanges.length > 0) {
        matchingOrgEntChangeIdsByEntId.set(entitlementId, matchingChanges);
      }
    });

  const addSkuDep = (skuName: string, depId: string) => {
    if (!skuDeps.has(skuName)) { skuDeps.set(skuName, new Set()); }
    skuDeps.get(skuName)?.add(depId);
  };

  result.insertPayload.bulk_entry.forEach((group) => {
    switch (group.table_key) {
      case 'products':
        creates.push({
          id: ids.productCreate,
          entity: 'product',
          kind: 'create',
          label: str(group.entries[0]?.data.Name),
          detail: 'New product',
          dependsOn: [],
          locked: true,
        });
        break;
      case 'entitlements':
        group.entries.forEach((e) => {
          creates.push({
            id: ids.entCreate(entKeyQ(e.data.Name, e.data.Value, e.data.NeedsQuantity)),
            entity: 'entitlement',
            kind: 'create',
            label: str(e.data.Name),
            detail: str(e.data.Value),
            dependsOn: [],
          });
        });
        break;
      case 'org_entitlements':
        group.entries.forEach((e) => {
          creates.push({
            id: ids.orgEntCreate(orgEntKey(e.data.Name, e.data.Value, e.data.AccessType)),
            entity: 'orgEntitlement',
            kind: 'create',
            label: str(e.data.Name),
            detail: `${str(e.data.Value)} (${str(e.data.AccessType)})`,
            dependsOn: [],
          });
        });
        break;
      case 'provisioning_questions':
        group.entries.forEach((e) => {
          creates.push({
            id: ids.questionCreate(str(e.data.Name)),
            entity: 'question',
            kind: 'create',
            label: str(e.data.Name),
            detail: str(e.data.Type),
            dependsOn: [],
          });
        });
        break;
      case 'product_allowed_regions':
        group.entries.forEach((e) => {
          creates.push({
            id: ids.regionCreate(str(e.data.RegionName)),
            entity: 'region',
            kind: 'create',
            label: str(e.data.RegionName),
            dependsOn: [],
          });
        });
        break;
      case 'skus':
        group.entries.forEach((e) => skuCreateNames.add(str(e.data.Name)));
        break;
      case 'product_sku_configs':
        group.entries.forEach((e) => {
          const skuName = maps.skuIdToName.get(num(e.data.SkuID) ?? -1);
          if (skuName) { skuCreateNames.add(skuName); }
        });
        break;
      case 'product_sku_config_entitlements':
        group.entries.forEach((e) => {
          const skuName = maps.skuIdToName.get(num(e.data.ProductSkuConfigSkuID) ?? -1);
          if (skuName) { skuCreateNames.add(skuName); }
          const entId = num(e.data.EntitlementID);
          const entKeyValue = entId !== undefined ? maps.newEntIdToKey.get(entId) : undefined;
          if (skuName && entKeyValue !== undefined) {
            addSkuDep(skuName, ids.entCreate(entKeyValue));
            (matchingOrgEntChangeIdsByEntId.get(entId as number) ?? []).forEach((changeId) =>
              addSkuDep(skuName, changeId),
            );
          }
        });
        break;
      case 'sku_org_entitlement_configs':
        group.entries.forEach((e) => {
          const skuName = maps.skuIdToName.get(num(e.data.SkuID) ?? -1);
          if (skuName) { skuCreateNames.add(skuName); }
          const oeId = num(e.data.OrgEntitlementID);
          const orgEntKeyValue = oeId !== undefined ? maps.newOrgEntIdToKey.get(oeId) : undefined;
          if (skuName && orgEntKeyValue !== undefined) {
            addSkuDep(skuName, ids.orgEntCreate(orgEntKeyValue));
          }
        });
        break;
      default:
        break;
    }
  });

  Array.from(skuCreateNames)
    .sort()
    .forEach((name) => {
      creates.push({
        id: ids.skuCreate(name),
        entity: 'sku',
        kind: 'create',
        label: name,
        detail: 'Add or update SKU configuration',
        dependsOn: Array.from(skuDeps.get(name) ?? []),
      });
    });

  // ── Updates from the update payload ──────────────────────────────────────
  if (result.summary.productUpdated) {
    const changed = Object.keys(result.summary.productFieldDiffs);
    updates.push({
      id: ids.productUpdate,
      entity: 'product',
      kind: 'update',
      label: 'Product fields',
      detail: changed.join(', '),
      dependsOn: [],
    });
  }

  (result.updatePayload?.bulk_update ?? []).forEach((group) => {
    group.updates.forEach((u) => {
      switch (group.table_key) {
        case 'entitlements':
          updates.push({
            id: ids.entUpdate(entKeyQ(u.data.Name, u.data.Value, u.data.NeedsQuantity)),
            entity: 'entitlement',
            kind: 'update',
            label: str(u.data.Name),
            detail: str(u.data.Value),
            dependsOn: [],
          });
          break;
        case 'org_entitlements':
          updates.push({
            id: ids.orgEntUpdate(orgEntKey(u.data.Name, u.data.Value, u.data.AccessType)),
            entity: 'orgEntitlement',
            kind: 'update',
            label: str(u.data.Name),
            detail: `${str(u.data.Value)} (${str(u.data.AccessType)})`,
            dependsOn: [],
          });
          break;
        case 'provisioning_questions':
          updates.push({
            id: ids.questionUpdate(str(u.data.Name)),
            entity: 'question',
            kind: 'update',
            label: str(u.data.Name),
            dependsOn: [],
          });
          break;
        default:
          break;
      }
    });
  });

  // ── Deletes from the delete payload ──────────────────────────────────────
  const dp = result.deletePayload;

  dp.entitlementsToDelete.forEach((d) => {
    deletes.push({
      id: ids.entDelete(entKeyQ(d.name, d.value, d.needs_quantity)),
      entity: 'entitlement',
      kind: 'delete',
      label: d.name,
      detail: d.value,
      dependsOn: [],
    });
  });
  dp.orgEntitlementsToDelete.forEach((d) => {
    deletes.push({
      id: ids.orgEntDelete(entKey(d.name, d.value)),
      entity: 'orgEntitlement',
      kind: 'delete',
      label: d.name,
      detail: d.value,
      dependsOn: [],
    });
  });
  dp.regionsToDelete.forEach((d) => {
    deletes.push({
      id: ids.regionDelete(d.regionName),
      entity: 'region',
      kind: 'delete',
      label: d.regionName,
      dependsOn: [],
    });
  });
  dp.questionsToDelete.forEach((d) => {
    deletes.push({
      id: ids.questionDelete(d.name),
      entity: 'question',
      kind: 'delete',
      label: d.name,
      dependsOn: [],
    });
  });
  dp.skuLinksToRemove.forEach((d) => {
    deletes.push({
      id: ids.skuUnlink(d.skuName),
      entity: 'sku',
      kind: 'delete',
      label: d.skuName,
      detail: 'Remove SKU from product',
      dependsOn: [],
    });
  });

  // SKUs that keep their link but have stale entitlement/org-entitlement links removed
  const linkChangeSkuNames = new Set<string>();
  const collectLinkChange = (skuId: number) => {
    const name = maps.skuIdToName.get(skuId);
    if (name && !maps.unlinkSkuNames.has(name)) { linkChangeSkuNames.add(name); }
  };
  dp.skuConfigEntitlementsToDelete.forEach((d) => collectLinkChange(d.skuId));
  dp.skuOrgEntitlementConfigsToDelete.forEach((d) => collectLinkChange(d.skuId));
  Array.from(linkChangeSkuNames)
    .sort()
    .forEach((name) => {
      deletes.push({
        id: ids.skuLinkChange(name),
        entity: 'skuLinkChange',
        kind: 'delete',
        label: name,
        detail: 'Remove stale entitlement / org links',
        dependsOn: [],
      });
    });

  return { creates, updates, deletes };
}

// ─────────────────────────────────────────────────────────────────────────────
// Default selection — creates & updates on, deletes off (opt-in removal)
// ─────────────────────────────────────────────────────────────────────────────

export function defaultSelectedIds(plan: ImportPlan): Set<string> {
  const selected = new Set<string>();
  plan.creates.forEach((c) => selected.add(c.id));
  plan.updates.forEach((u) => selected.add(u.id));
  // deletes intentionally left unselected
  return selected;
}

/** All ids in the plan (for select-all helpers). */
export function allPlanIds(plan: ImportPlan): string[] {
  return [...plan.creates, ...plan.updates, ...plan.deletes].map((i) => i.id);
}

// ─────────────────────────────────────────────────────────────────────────────
// filterImportResult — keep only rows attributed to a selected ChangeItem
// ─────────────────────────────────────────────────────────────────────────────

export function filterImportResult(
  result: ImportPayloadResult,
  target: TargetEnvData,
  selectedIds: Set<string>,
): ImportPayloadResult {
  const maps = buildLookups(result, target);
  const isSel = (id: string) => selectedIds.has(id);
  const skuSelected = (name: string | undefined) =>
    !!name && isSel(ids.skuCreate(name));

  // ── Filter inserts ───────────────────────────────────────────────────────
  const bulk_entry = result.insertPayload.bulk_entry
    .map((group) => {
      let entries = group.entries;
      switch (group.table_key) {
        case 'products':
          break; // always applied
        case 'entitlements':
          entries = entries.filter((e) =>
            isSel(ids.entCreate(entKeyQ(e.data.Name, e.data.Value, e.data.NeedsQuantity))),
          );
          break;
        case 'org_entitlements':
          entries = entries.filter((e) =>
            isSel(ids.orgEntCreate(orgEntKey(e.data.Name, e.data.Value, e.data.AccessType))),
          );
          break;
        case 'provisioning_questions':
          entries = entries.filter((e) => isSel(ids.questionCreate(str(e.data.Name))));
          break;
        case 'product_allowed_regions':
          entries = entries.filter((e) => isSel(ids.regionCreate(str(e.data.RegionName))));
          break;
        case 'skus':
          entries = entries.filter((e) => skuSelected(str(e.data.Name)));
          break;
        case 'product_sku_configs':
          entries = entries.filter((e) =>
            skuSelected(maps.skuIdToName.get(num(e.data.SkuID) ?? -1)),
          );
          break;
        case 'product_sku_config_entitlements':
          entries = entries.filter((e) => {
            const skuName = maps.skuIdToName.get(num(e.data.ProductSkuConfigSkuID) ?? -1);
            if (!skuSelected(skuName)) { return false; }
            const entId = num(e.data.EntitlementID);
            const entKeyValue = entId !== undefined ? maps.newEntIdToKey.get(entId) : undefined;
            if (entKeyValue !== undefined) {
              return isSel(ids.entCreate(entKeyValue));
            }
            return true; // references a pre-existing entitlement
          });
          break;
        case 'sku_org_entitlement_configs':
          entries = entries.filter((e) => {
            const skuName = maps.skuIdToName.get(num(e.data.SkuID) ?? -1);
            if (!skuSelected(skuName)) { return false; }
            const oeId = num(e.data.OrgEntitlementID);
            const orgEntKeyValue = oeId !== undefined ? maps.newOrgEntIdToKey.get(oeId) : undefined;
            if (orgEntKeyValue !== undefined) {
              return isSel(ids.orgEntCreate(orgEntKeyValue));
            }
            return true;
          });
          break;
        default:
          break;
      }
      return { table_key: group.table_key, entries };
    })
    .filter((group) => group.entries.length > 0);

  // ── Filter updates ───────────────────────────────────────────────────────
  const bulk_update = (result.updatePayload?.bulk_update ?? [])
    .map((group) => {
      let updates = group.updates;
      switch (group.table_key) {
        case 'products':
          updates = isSel(ids.productUpdate) ? updates : [];
          break;
        case 'entitlements':
          updates = updates.filter((u) =>
            isSel(ids.entUpdate(entKeyQ(u.data.Name, u.data.Value, u.data.NeedsQuantity))),
          );
          break;
        case 'org_entitlements':
          updates = updates.filter((u) =>
            isSel(ids.orgEntUpdate(orgEntKey(u.data.Name, u.data.Value, u.data.AccessType))),
          );
          break;
        case 'provisioning_questions':
          updates = updates.filter((u) => isSel(ids.questionUpdate(str(u.data.Name))));
          break;
        default:
          break;
      }
      return { table_key: group.table_key, updates };
    })
    .filter((group) => group.updates.length > 0);

  // ── Filter deletes ───────────────────────────────────────────────────────
  const dp = result.deletePayload;

  const entDeleteSelected = (id: number) => {
    const k = maps.entDeleteIdToKey.get(id);
    return !!k && isSel(ids.entDelete(k));
  };
  const orgEntDeleteSelected = (id: number) => {
    const k = maps.orgEntDeleteIdToKey.get(id);
    return !!k && isSel(ids.orgEntDelete(k));
  };
  const skuChildDeleteSelected = (skuId: number, parentEntDeleteSelected: boolean) => {
    const name = maps.skuIdToName.get(skuId);
    if (name && maps.unlinkSkuNames.has(name)) {
      return isSel(ids.skuUnlink(name));
    }
    if (name && isSel(ids.skuLinkChange(name))) { return true; }
    // FK safety: a child config referencing an entitlement/org-ent being deleted
    // must be removed first regardless of the SKU-level toggle.
    return parentEntDeleteSelected;
  };

  const deletePayload: DeletePayload = {
    skuOrgEntitlementConfigsToDelete: dp.skuOrgEntitlementConfigsToDelete.filter((d) =>
      skuChildDeleteSelected(d.skuId, orgEntDeleteSelected(d.orgEntitlementId)),
    ),
    skuConfigEntitlementsToDelete: dp.skuConfigEntitlementsToDelete.filter((d) =>
      skuChildDeleteSelected(d.skuId, entDeleteSelected(d.entitlementId)),
    ),
    skuLinksToRemove: dp.skuLinksToRemove.filter((d) => isSel(ids.skuUnlink(d.skuName))),
    otherProductLinksToRemove: dp.otherProductLinksToRemove.filter((d) =>
      isSel(ids.skuUnlink(d.skuName)),
    ),
    regionsToDelete: dp.regionsToDelete.filter((d) => isSel(ids.regionDelete(d.regionName))),
    questionsToDelete: dp.questionsToDelete.filter((d) => isSel(ids.questionDelete(d.name))),
    orgEntitlementsToDelete: dp.orgEntitlementsToDelete.filter((d) =>
      isSel(ids.orgEntDelete(entKey(d.name, d.value))),
    ),
    entitlementsToDelete: dp.entitlementsToDelete.filter((d) =>
      isSel(ids.entDelete(entKeyQ(d.name, d.value, d.needs_quantity))),
    ),
  };

  // ── Recompute summary counts to reflect the selection ────────────────────
  const countTable = (
    groups: Array<{ table_key: string; entries?: unknown[]; updates?: unknown[] }>,
    table: string,
    field: 'entries' | 'updates',
  ) => groups.find((g) => g.table_key === table)?.[field]?.length ?? 0;

  const summary = {
    ...result.summary,
    productUpdated: result.summary.productUpdated && isSel(ids.productUpdate),
    entitlementsCreated: countTable(bulk_entry, 'entitlements', 'entries'),
    entitlementsUpdated: countTable(bulk_update, 'entitlements', 'updates'),
    entitlementsDeleted: deletePayload.entitlementsToDelete.length,
    orgEntitlementsCreated: countTable(bulk_entry, 'org_entitlements', 'entries'),
    orgEntitlementsUpdated: countTable(bulk_update, 'org_entitlements', 'updates'),
    orgEntitlementsDeleted: deletePayload.orgEntitlementsToDelete.length,
    questionsCreated: countTable(bulk_entry, 'provisioning_questions', 'entries'),
    questionsUpdated: countTable(bulk_update, 'provisioning_questions', 'updates'),
    questionsDeleted: deletePayload.questionsToDelete.length,
    regionsLinked: countTable(bulk_entry, 'product_allowed_regions', 'entries'),
    regionsDeleted: deletePayload.regionsToDelete.length,
    skusCreated: countTable(bulk_entry, 'skus', 'entries'),
    skuLinksCreated: countTable(bulk_entry, 'product_sku_configs', 'entries'),
    skuLinksRemoved: deletePayload.skuLinksToRemove.length,
  };

  return {
    insertPayload: { bulk_entry },
    updatePayload: bulk_update.length > 0 ? { bulk_update } : null,
    deletePayload,
    summary,
  };
}
